import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import PageviewOutlinedIcon from '@mui/icons-material/PageviewOutlined';
import TableGridWithFilters from '../../components/TablegridWithFilters/TableGridWithFilters/TableGridWithFilters';
import LinkButton from '../../components/Buttons/LinkButton';
import TabsLayout from '../../components/Tabs/TabsLayout';
import Theme from '../../Themes/main.theme';
import UserService from '../../services/UserService';
import { PermissionsRolesManagement } from '../../lib/permissions';

function RoleHandling() {
  const [tab, setTab] = useState<any>(0);
  const navigate = useNavigate();


  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Ονομα Ρόλου',
      minWidth: 450,
    },
    {
      field: 'description',
      headerName: 'Περιγραφή Ρόλου',
      minWidth: 580, 
    },
    {
      field: 'action',
      headerName: 'Ενέργειες',
      minWidth: 180,
      sortable: false,
      renderCell: ({ row }) => <>
        <Tooltip PopperProps={{
          disablePortal: true
        }} title={'Προβολή Ρόλου'}>
          <IconButton
            color='primary'
            onClick={() => navigate(`/admin/roles/view-internal/${row.id}`)}>
            <PageviewOutlinedIcon />
          </IconButton>
        </Tooltip>
        <Tooltip PopperProps={{
          disablePortal: true
        }} title={'Επεξεργασία Εσωτερικού Ρόλου'}>
          <IconButton disabled={!UserService.hasRole([PermissionsRolesManagement.PermissionsInternalRoleManagementCreate])} aria-label='edit'
            color='primary'
            onClick={() => navigate(`/admin/roles/edit-internal/${row.id}`)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      </>
    },
  ];

  const columnsOut: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Ονομα Ρόλου',
      minWidth: 450
    },
    {
      field: 'description',
      headerName: 'Περιγραφή Ρόλου',
      minWidth: 580, 
    },
    {
      field: 'action',
      headerName: 'Ενέργειες',
      minWidth: 180,
      sortable: false,
      renderCell: ({ row }) => <>
        <Tooltip PopperProps={{
          disablePortal: true
        }} title={'Προβολή Εξωτερικού Ρόλου'}>
          <IconButton
            color='primary'
            onClick={() => navigate(`/admin/roles/view-external/${row.id}`)}>
            <PageviewOutlinedIcon />
          </IconButton>
        </Tooltip>
        <Tooltip PopperProps={{
          disablePortal: true
        }} title={'Επεξεργασία Ρόλου'}>
          <IconButton
            disabled={!UserService.hasRole([
              PermissionsRolesManagement.PermissionsInternalRoleManagementCreate
            ])}
            aria-label='edit'
            color='primary'
            onClick={() => navigate(`/admin/roles/edit-external/${row.id}`)}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      </>
    },
  ];

  const tabLabels = [
    {
      label: <Typography variant='h4'>Εσωτερικοί Ρόλοι</Typography>,
    },
    {
      label: <Typography variant='h4'>Εξωτερικοί Ρόλοι</Typography>,
    }
  ];

  const tab1 = <Box>
    <Box display='flex' justifyContent='end'
      mt={5} 
      gap={2}
      flexWrap='wrap'>
      <LinkButton disabled={!UserService.hasRole([PermissionsRolesManagement.PermissionsInternalRoleManagementCreate])} link='/admin/roles/new-internal'
        color='primary'
        variant='contained' sx={{ borderRadius: 24, maxWidth: '300px', maxHeight: '55px', minWidth: '250px', minHeight: '55px' }}>Νέος Εσωτερικός Ρόλος</LinkButton>
      <LinkButton disabled={!UserService.hasRole([PermissionsRolesManagement.PermissionsInternalRoleManagementCreate])} link='/admin/roles/new-external'
        color='primary'
        variant='contained' sx={{ borderRadius: 24, maxWidth: '300px', maxHeight: '55px', minWidth: '250px', minHeight: '55px' }}>Νέος Εξωτερικός Ρόλος</LinkButton>
    </Box>
    <TableGridWithFilters
      responseTotalCountFieldName='total_count'
      hideFilters
      pageSize={15}
      rowsPerPageOptions={[15, 30, 45]}
      sortingMode='server'
      paginationMode='server'
      responseDataFieldName=''
      columns={columns}
      fetchUrl={`${process.env.REACT_APP_BACKEND_URL}/authorization/groups/internal`}
    />
  </Box>;

  const tab2 = <Box>
    <Box display='flex' justifyContent='end'
      mt={5} >
      <LinkButton link='/admin/roles/new-internal' color='primary'
        disabled={!UserService.hasRole([PermissionsRolesManagement.
          PermissionsInternalRoleManagementCreate])}
        variant='contained' sx={{ mr: 5, borderRadius: 24, maxWidth: '300px', maxHeight: '55px', minWidth: '250px', minHeight: '55px' }}>Νέος Εσωτερικός Ρόλος</LinkButton>
      <LinkButton link='/admin/roles/new-external' color='primary'
        disabled={!UserService.hasRole([PermissionsRolesManagement.
          PermissionsInternalRoleManagementCreate])}
        variant='contained' sx={{ borderRadius: 24, maxWidth: '300px', maxHeight: '55px', minWidth: '250px', minHeight: '55px' }}>Νέος Εξωτερικός Ρόλος</LinkButton>
    </Box>
    <TableGridWithFilters
      responseDataFieldName=''
      responseTotalCountFieldName='total_count'
      hideFilters
      reFetch={tab}
      pageSize={15}
      rowsPerPageOptions={[15, 30, 45]}
      sortingMode='server'
      paginationMode='server'
      columns={columnsOut}
      fetchUrl={`${process.env.REACT_APP_BACKEND_URL}/authorization/groups/external`}
    />
  </Box>;
  const tabContent = [
    tab1,
    tab2
  ];

  return (
    <>
      <Typography sx={{ my: 2, mt: 4 }} variant='h2'
        color={Theme?.labelColor?.main}>Διαχείριση Ρόλων</Typography>
      <Divider sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }} />
      <TabsLayout
        tab={tab}
        setTab={setTab}
        tabLabels={tabLabels}
        tabContent={tabContent}
      />
    </>
  );
}
export default RoleHandling;